/* eslint-disable max-lines */
import React from 'react';
import { createTheme } from '@material-ui/core/styles';
import { MuiThemeProvider } from '@material-ui/core';
import { getColor } from './constants/_colors';
import { SIZES, SPACES } from './constants/sizes';

const makeMaterialColor = (colorName, alpha) => ({
  main: getColor(colorName, alpha),
  500: getColor(colorName, alpha)
});

const palette = {
  success: makeMaterialColor('malachite'),
  error: makeMaterialColor('torchRed'),
  warning: makeMaterialColor('orangePeel'),
  chambray: makeMaterialColor('chambray'),
  seniority: makeMaterialColor('orangePeel', 0.8),
  done: makeMaterialColor('torchRed', 0.8),
  skills: makeMaterialColor('fog', 0.8),
  lb: makeMaterialColor('botticelli'),
  fog: makeMaterialColor('fog', 0.8),
  secondary: makeMaterialColor('malachite'),
  draft: makeMaterialColor('silverSand')
};

const themeOptions = {
  typography: {
    fontFamily: 'inherit'
  },
  palette,
  overrides: {
    MuiPopover: {
      root: {
        zIndex: '1400 !important'
      }
    },
    MuiAutocomplete: {
      root: {
        '& .MuiFormControl-marginNormal': {
          margin: '0 !important'
        },
        '&:focus, &:hover:not(.Mui-disabled)': {
          backgroundColor: getColor('zircon'),
          '& .MuiAutocomplete-input': {
            '&::placeholder': {
              color: `${getColor('waikawaGray')} !important`
            }
          }
        }
      },
      endAdornment: {
        position: 'unset !important'
      },
      inputRoot: {
        border: `0px !important`,
        width: '100% !important',
        height: '100% !important',
        padding: '0.44rem 1rem 0.44rem 1rem !important',
        borderRadius: 4,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: getColor('botticelli')
        },
        '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${getColor('dodgerBlue')} !important`
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${getColor('dodgerBlue')} !important`
        },
        '& .MuiButtonBase-root, & .MuiButtonBase-root:hover, & .MuiButtonBase-root:focus, , & .MuiButtonBase-root:active':
          {
            background: 'unset !important',
            '& button.MuiAutocomplete-popupIndicator svg': {
              fontSize: '1.6rem'
            }
          }
      },
      input: {
        padding: '0px !important',
        margin: '0px !important',
        color: 'inherit !important',
        '&::placeholder': {
          opacity: '1'
        }
      },
      option: {
        fontSize: SIZES.medium,
        height: '3.7rem',
        lineHeight: '3.7rem',
        margin: '0.5rem',
        padding: '0 !important',
        paddingLeft: '0.5rem !important',
        whiteSpace: 'nowrap',
        '&:hover': {
          backgroundColor: getColor('catskillGray')
        }
      },
      popper: {
        border: '0px !important',
        borderRadius: '4px',
        boxShadow:
          '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)'
      },
      paper: {
        margin: 0,
        boxShadow: 'unset !important'
      }
    },
    MuiTableHead: {
      root: {
        '&> .MuiTableRow-root': {
          '&:first-child': {
            borderTop: `1px solid ${getColor('gray', 0.2)}`,

            '&:not(:only-child)': {
              '&> .MuiTableCell-root': {
                borderBottom: '0'
              }
            }
          }
        }
      }
    },
    MuiTableRow: {
      root: {
        '&:not(.MuiTableRow-head):not([disabled]):hover': {
          backgroundColor: `${getColor('catskillGray')} !important`
        }
      }
    },
    MuiTableCell: {
      stickyHeader: {
        backgroundColor: 'white'
      },
      paddingCheckbox: {
        width: '35px'
      },
      root: {
        padding: '0.8rem 1.2rem',
        border: `1px solid ${getColor('catskillWhite')}`,
        height: '4rem',
        borderTop: 'none',
        '&:last-child': {
          borderRight: '0'
        },
        '&:first-child': {
          borderLeft: '0'
        }
      },
      head: {
        height: '2rem !important'
      },
      alignRight: {
        '& div': {
          justifyContent: 'end',
          textAlign: 'right'
        }
      },
      alignCenter: {
        '& div': {
          justifyContent: 'center'
        }
      }
    },
    MuiInputBase: {
      input: {
        fontSize: SIZES.medium
      }
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 0
      },
      input: {
        padding: SPACES.small,
        height: SIZES['x-x-x-large']
      }
    },
    MuiTooltip: {
      popper: {
        zIndex: '1399',
        '&#increaseTooltipZIndex': {
          zIndex: '1401 !important'
        }
      },
      tooltip: {
        fontSize: SIZES.medium,
        paddingRight: '1rem',
        paddingLeft: '1rem',
        color: `${getColor('shark')}`,
        fontWeight: 'normal',
        maxWidth: 'inherits',
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
        border: '1px solid rgba(84, 103, 255, 0.2)',
        boxShadow: '0 0 30px rgba(59, 89, 152, 0.15)',
        borderRadius: '0.5rem'
      }
    },
    MuiAvatar: {
      root: {
        width: '3rem',
        height: '3rem'
      }
    },
    MuiChip: {
      root: {
        borderRadius: '4px'
      },
      deleteIcon: {
        height: 'unset !important',
        marginRight: '0.7rem !important',
        '&:hover svg': {
          color: `${getColor('dodgerBlue')} !important`
        }
      },
      label: {
        display: 'flex',
        alignItems: 'center',
        overflow: 'unset',
        textOverflow: 'unset'
      },
      outlined: {
        borderWidth: '1px !important',
        backgroundColor: 'transparent !important',
        '&:hover': {
          backgroundColor: `${getColor('zircon')} !important`
        }
      }
    },
    MuiFormControl: {
      root: {
        width: '100%'
      }
    },
    MuiFormControlLabel: {
      root: {
        '&.topAligned': {
          display: 'flex',
          alignItems: 'flex-start'
        }
      },
      label: {
        fontSize: SIZES.large,
        color: getColor('chambray'),
        width: '100%'
      }
    },
    MuiIconButton: {
      root: {
        '&:hover': {
          '& svg  > path[stroke]': {
            stroke: getColor('dodgerBlue')
          },
          '& svg  > path[fill]': {
            fill: getColor('dodgerBlue')
          }
        }
      }
    },
    MuiButton: {
      root: {
        fontSize: SIZES.medium,
        padding: '0.6rem 1.1rem !important',
        textTransform: 'none',
        boxShadow: 'none !important',
        '&:hover': {
          boxShadow: 'none !important'
        },
        lineHeight: 'normal',
        fontWeight: 'unset'
      },
      sizeSmall: {
        padding: '0.5rem 1rem !important',
        fontSize: `${SIZES.small} !important`
      },
      sizeLarge: {
        padding: '0.8rem 1.2rem !important',
        fontSize: `${SIZES.medium} !important`
      },
      outlined: {
        background: `${getColor('white')} !important`,
        borderColor: `${getColor('botticelli')} !important`,
        color: `${getColor('chambray')} !important`,
        '&.Mui-disabled': {
          borderColor: `${getColor('botticelli')} !important`,
          backgroundColor: `${getColor('catskillGray')} !important`,
          color: `${getColor('silverSand')} !important`
        },
        '&:hover': {
          borderColor: `${getColor('dodgerBlue')} !important`,
          backgroundColor: `${getColor('zircon')} !important`,
          color: `${getColor('dodgerBlue')} !important`,
          '& svg  > path[stroke]': {
            stroke: `${getColor('dodgerBlue')} !important`
          },
          '& svg  > path[fill]': {
            fill: `${getColor('dodgerBlue')} !important`
          },
          '& svg': {
            color: `${getColor('dodgerBlue')} !important`
          }
        },
        '&:focus': {
          borderColor: `${getColor('dodgerBlue')} !important`,
          backgroundColor: `${getColor('white')} !important`,
          color: `${getColor('dodgerBlue')} !important`,
          '& svg  > path[stroke]': {
            stroke: `${getColor('dodgerBlue')} !important`
          },
          '& svg  > path[fill]': {
            fill: `${getColor('dodgerBlue')} !important`
          }
        }
      },
      contained: {
        backgroundColor: getColor('chambray'),
        border: `1px solid transparent !important`,
        color: 'white',
        '&:hover': {
          backgroundColor: `${getColor('darkBlue')} !important`,
          color: 'white'
        },
        '&:focus': {
          backgroundColor: `${getColor('dodgerBlue')} !important`,
          color: 'white'
        },
        '&.Mui-disabled': {
          backgroundColor: `${getColor('catskillGray')} !important`,
          color: `${getColor('silverSand')} !important`
        }
      }
    },
    MuiInputLabel: {
      root: {
        fontSize: SIZES.large
      },
      outlined: {
        zIndex: 'unset !important'
      }
    },
    MuiTab: {
      root: {
        textTransform: 'none',
        minWidth: '8rem !important',
        fontSize: `${SIZES.medium} !important`
        // '&:hover': {
        //   color: '#40a9ff',
        //   opacity: 1,
        // },
        // '&$selected': {
        //   color: '#1890ff',
        //   fontWeight: 600,
        // },
        // '&:focus': {
        //   color: '#40a9ff',
        // },
      }
    },
    MuiSnackbar: {
      anchorOriginBottomCenter: {
        right: '0 !important',
        left: '0 !important',
        transform: 'none !important'
      }
    },
    MuiLink: {
      root: {
        cursor: 'pointer',
        color: getColor('chambray'),
        '&:hover': {
          color: getColor('dodgerBlue')
        }
      },
      underlineHover: {
        textDecoration: 'none !important'
      }
    }
  }
};

const ThemeProvider = ({ children }) => (
  <MuiThemeProvider theme={createTheme(themeOptions)}>
    {children}
  </MuiThemeProvider>
);

export default ThemeProvider;

export const providers = {
  ...[
    'success',
    'error',
    'warning',
    'chambray',
    'seniority',
    'done',
    'skills',
    'lb',
    'fog',
    'draft'
  ].reduce(
    (acc, val) => ({
      ...acc,
      [val]: ({ children }) => (
        <MuiThemeProvider
          theme={createTheme({
            ...themeOptions,
            palette: { ...palette, primary: palette[val] }
          })}
        >
          {children}
        </MuiThemeProvider>
      )
    }),
    {}
  )
};
